import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountType,
  getV1CompaniesCompanyIdAccounts,
  getV1CompaniesCompanyIdDrivers,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { createContext, useCallback, useMemo } from 'react';

import { FetchReason } from '~constants/enums';
import { Account } from '~hooks/useAccount/models';
import { Driver } from '~hooks/useDrivers/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';

function getOwnerFetchFailureMessage(ownerType: RateOwnerType) {
  return ownerType === RateOwnerType.DRIVER
    ? t('error_messages.drivers.failed_to_fetch_drivers')
    : t('error_messages.account.failed_to_fetch_company_accounts');
}

type ScheduleOwnerQueryContextType = {
  clear: () => void;
  data: Account[] | Driver[];
  fetchMore: () => void;
  fetchReason?: FetchReason;
  hasMore: boolean;
  isError: boolean;
  refresh: () => void;
};

const ScheduleOwnerQueryContext = createContext<ScheduleOwnerQueryContextType>({
  clear: noop,
  data: [],
  fetchMore: noop,
  hasMore: false,
  isError: false,
  refresh: noop,
});

interface ScheduleOwnerQueryContextProviderProps {
  companyId: string;
  ownerType: RateOwnerType;
  searchQuery: string;
}

function ScheduleOwnerQueryContextProvider({
  children,
  companyId,
  ownerType,
  searchQuery,
}: React.PropsWithChildren<ScheduleOwnerQueryContextProviderProps>) {
  const queryKey = 'ScheduleOwnerQueryContextProvider_schedules';
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: [queryKey, ownerType, companyId, searchQuery],
    initialPageParam: undefined as string | undefined,
    // The fetch reports its own failures, and the app-wide client retries three
    // times, which would report the same one four times over.
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      try {
        const query = {
          'page[limit]': 20,
          ...(pageParam ? { 'page[after]': pageParam } : {}),
          ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
        };

        let response;
        let scheduleOwners: Account[] | Driver[];
        switch (ownerType) {
          case RateOwnerType.VENDOR:
            response = await getV1CompaniesCompanyIdAccounts({
              path: { 'company-id': companyId },
              query: {
                'filter[account_types]': [AccountType.VENDOR],
                'filter[auto_add_ons]': true,
                'sort[name]': 'asc',
                ...query,
              },
              signal,
            });

            scheduleOwners = response.data.data.map((item) => Account.parse(item));
            break;
          case RateOwnerType.CUSTOMER:
            response = await getV1CompaniesCompanyIdAccounts({
              path: { 'company-id': companyId },
              query: {
                'filter[account_types]': [AccountType.CUSTOMER],
                'filter[auto_add_ons]': true,
                'sort[name]': 'asc',
                ...query,
              },
              signal,
            });
            scheduleOwners = response.data.data.map((item) => Account.parse(item));
            break;
          case RateOwnerType.DRIVER:
            response = await getV1CompaniesCompanyIdDrivers({
              path: { 'company-id': companyId },
              query: {
                'filter[auto_add_ons]': true,
                ...query,
              },
              signal,
            });
            scheduleOwners = response.data.data.map((item) => Driver.parse(item));
            break;
        }

        return {
          nextCursor: extractPagination(response).after,
          scheduleOwners,
        };
      } catch (error) {
        // A superseded search term, owner tab or company aborts the in-flight
        // request; that is routine, not a failure to report.
        if (axios.isCancel(error)) {
          throw error;
        }

        connection.handleRequestError(error, getOwnerFetchFailureMessage(ownerType));
        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const hasMore = query.hasNextPage;

  const data = useMemo(
    () => (query.data?.pages ?? []).map((page) => page.scheduleOwners).flat(),
    [query.data?.pages],
  );

  const fetchReason = useMemo(() => {
    if (query.isFetchingNextPage) {
      return FetchReason.INFINITE_SCROLL;
    }
    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetchingNextPage, query.isFetching]);

  const fetchMore = useCallback(() => {
    if (hasMore) {
      query.fetchNextPage();
    }
  }, [hasMore]);

  const clear = useCallback(() => {
    queryClient.removeQueries({
      predicate: (query) => String(query.queryKey[0]) === queryKey,
    });
  }, [queryKey]);

  const refresh = useCallback(() => {
    clear();
    query.refetch();
  }, [clear, query]);

  return (
    <ScheduleOwnerQueryContext.Provider
      value={{
        clear,
        data: data as Account[] | Driver[],
        fetchMore,
        fetchReason,
        hasMore,
        isError: query.isError,
        refresh,
      }}
    >
      {children}
    </ScheduleOwnerQueryContext.Provider>
  );
}

export default ScheduleOwnerQueryContext;
export { getOwnerFetchFailureMessage, ScheduleOwnerQueryContextProvider };
